import type { CSSProperties, ReactNode } from 'react';
import { Fragment } from 'react';
import type { DPDay, DPPropGetter } from '@rehookify/datepicker';
import { useContextDaysPropGetters } from '@rehookify/datepicker';

import type { BoxProps } from '../../Box/Box';
import { Box } from '../../Box/Box';

const getColor = (day: DPDay) => {
  const { selected, disabled, range } = day;

  if (disabled) {
    return 'secondaryContentOnDisabled';
  }

  if (selected) {
    return 'primaryContentOnEnabled';
  }

  if (range === 'will-be-range-start') {
    return 'default';
  }

  if (range === 'will-be-range-end') {
    return 'default';
  }

  return 'inherit';
};

const getContainerBorderTopLeftRadius = (day: DPDay) => {
  const { range, selected, now } = day;

  if (!selected && !now && range === '') {
    return '6';
  }

  if (now) {
    return '6';
  }

  if (selected && range === '') {
    return '6';
  }

  if (now && range === 'will-be-range-end') {
    return '6';
  }

  if (selected && range === 'range-start') {
    return '6';
  }

  if (now && selected) {
    return '6';
  }

  if (range === 'will-be-range-start') {
    return '6';
  }

  return 'inherit';
};

const getContainerBorderTopRightRadius = (day: DPDay) => {
  const { range, selected, now } = day;

  if (!selected && !now && range === '') {
    return '6';
  }

  if (now) {
    return '6';
  }

  if (selected && range === '') {
    return '6';
  }

  if (now && selected) {
    return '6';
  }

  if (selected && range === 'range-end') {
    return '6';
  }

  if (range === 'will-be-range-end' && 'will-be-range-start' && 'in-range') {
    return '6';
  }

  return 'inherit';
};

const getContainerBorderBottomLeftRadius = (day: DPDay) => {
  const { range, selected, now } = day;

  if (!selected && !now && range === '') {
    return '6';
  }

  if (now) {
    return '6';
  }

  if (selected && range === '') {
    return '6';
  }

  if (now && range === 'will-be-range-end') {
    return '6';
  }

  if (now && selected) {
    return '6';
  }

  if (selected && range === 'range-start') {
    return '6';
  }

  if (range === 'will-be-range-start') {
    return '6';
  }

  return 'inherit';
};

const getContainerBorderBottomRightRadius = (day: DPDay) => {
  const { range, selected, now } = day;

  if (!selected && !now && range === '') {
    return '6';
  }

  if (now) {
    return '6';
  }

  if (selected && range === 'range-end') {
    return '6';
  }

  if (now && selected) {
    return '6';
  }

  if (selected && range === '') {
    return '6';
  }

  if (selected && range === 'in-range') {
    return 'inherit';
  }

  if (range === 'will-be-range-end') {
    return '6';
  }
  return 'inherit';
};

const getContainerBackgroundColor = (day: DPDay) => {
  const { selected, range, now, disabled } = day;

  if (disabled) {
    return 'inherit';
  }

  if (now && selected) {
    return 'primaryEnabled';
  }

  if (now && range === 'will-be-range-end') {
    return 'primaryEnabled';
  }

  if (now && range === 'will-be-in-range') {
    return 'primaryDisabled';
  }

  if (selected || range === 'will-be-range-end' || range === 'will-be-range-start') {
    return 'primaryDisabled';
  }

  return 'inherit';
};

const getBackgroundColor = (day: DPDay) => {
  const { selected, range, now, disabled } = day;

  if (disabled) {
    return 'inherit';
  }

  if (now) {
    return 'informative';
  }

  if (selected) {
    return 'primaryEnabled';
  }

  if (range === 'will-be-range-end') {
    return 'primaryEnabled';
  }

  if (range === 'will-be-range-start') {
    return 'primaryEnabled';
  }

  if (range === 'will-be-in-range') {
    return 'primaryDisabled';
  }

  if (range === 'in-range') {
    return 'primaryDisabled';
  }

  return 'inherit';
};

const getBorderRadius = (day: DPDay) => {
  const { selected, range, now } = day;

  if (now) {
    return 'round';
  }

  if (selected) {
    return '6';
  }

  if (range === 'will-be-range-start') {
    return '6';
  }

  if (range === 'will-be-range-end') {
    return '6';
  }

  return 'inherit';
};

const getPointer = (day: DPDay) => {
  const { disabled } = day;

  return disabled ? 'not-allowed' : 'pointer';
};

const getWidth = (day: DPDay) => {
  const { now } = day;

  if (now) {
    return '24px';
  }

  return '36px';
};

const getHeight = (day: DPDay) => {
  const { now } = day;

  if (now) {
    return '24px';
  }

  return '34px';
};

const containerStyles: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface Props {
  children: ReactNode;
  day: DPDay;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const Day = ({ children, day, onMouseEnter, onMouseLeave }: Props) => {
  const { dayButton } = useContextDaysPropGetters();

  const Wrapper = day.now ? Box : Fragment;

  const WrapperProps = day.now
    ? ({
        style: containerStyles,
        backgroundColor:
          day.range === 'will-be-in-range' ||
          day.range === 'in-range' ||
          day.range === 'will-be-range-end' ||
          day.selected
            ? 'primaryDisabled'
            : 'inherit',
        borderTopRightRadius: day.range === 'range-end' || day.range === 'will-be-range-end' ? '6' : 'inherit',
        borderBottomRightRadius: day.range === 'range-end' || day.range === 'will-be-range-end' ? '6' : 'inherit',
      } as BoxProps)
    : {};

  const dayProps: DPPropGetter & {
    onMouseEnter?: () => void;
  } = dayButton(day);

  return (
    <Wrapper {...WrapperProps}>
      <Box
        onMouseLeave={onMouseLeave}
        {...dayProps}
        onMouseEnter={function () {
          onMouseEnter?.();
          dayProps?.onMouseEnter?.();
        }}
        cursor={getPointer(day)}
        style={containerStyles}
        backgroundColor={{
          default: getContainerBackgroundColor(day),
          hover:
            day.disabled || day.selected || day.range === 'will-be-range-end' || day.range === 'will-be-range-start'
              ? undefined
              : 'primaryDisabled',
        }}
        borderTopLeftRadius={getContainerBorderTopLeftRadius(day)}
        borderTopRightRadius={getContainerBorderTopRightRadius(day)}
        borderBottomLeftRadius={getContainerBorderBottomLeftRadius(day)}
        borderBottomRightRadius={getContainerBorderBottomRightRadius(day)}
      >
        <Box
          color={getColor(day)}
          backgroundColor={getBackgroundColor(day)}
          padding="10"
          style={{
            width: getWidth(day),
            height: getHeight(day),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          borderTopLeftRadius={getBorderRadius(day)}
          borderBottomLeftRadius={getBorderRadius(day)}
          borderTopRightRadius={getBorderRadius(day)}
          borderBottomRightRadius={getBorderRadius(day)}
        >
          {children}
        </Box>
      </Box>
    </Wrapper>
  );
};

export { Day };
